import React, { useState, useEffect } from 'react';
import { FaEye, FaEyeSlash, FaGoogle, FaArrowLeft, FaPlus, FaCheck, FaMapMarkerAlt } from 'react-icons/fa';
import { Link, useNavigate } from 'react-router-dom';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import MultiSelect from '../../Components/utils/MultiSelect';
import Dropdown from '../../Components/utils/Dropdown';
import firstImage from '../../images/pngs/signUp1.png';
import firstBackground from '../../images/svgs/signUp3.svg';
import secondBackground from '../../images/svgs/signUp5.svg';
import secondImage from '../../images/pngs/signUp3.png';
import thirdImage from '../../images/pngs/signUp4.png';
import fourthImage from '../../images/pngs/signUp5.png';
import { showToast } from '../../Components/utils/ShowToast';
import axios from 'axios';
import countries from "../../data/countries.json"
import { states } from "../../data/constants"
import Loader from '../../Components/utils/Loader';
import languages from "../../data/languages.json"
import { getCurrencyForCountry } from '../../data/countryToCurrency';
import Logo from '../../images/pngs/Logo.png';
import SEO from '../../Components/utils/SEO';

const StepTab = ({ label, isActive, isCompleted, isClickable, onClick, text }) => (
  <div
    className={`step-tab ${isActive ? 'active' : ''} ${isCompleted ? 'completed' : ''} ${isClickable ? 'clickable' : 'disabled'}`}
    onClick={isClickable ? onClick : undefined}
  >
    <div className="step-number">
      {isCompleted ? <FaCheck className="check-icon" /> : <span>{label}</span>}
    </div>
    <div className="step-label">{text}</div>
  </div>
);

const SignUpForm = () => {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    email: '', password: '', confirmPassword: '', country: '', state: '', city: '',
    address: '', zipCode: '', fullName: '', username: '', phone: '', secondaryPhone: '', languages: ''
  });
  const [passwordError, setPasswordError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showSecondaryPhone, setShowSecondaryPhone] = useState(false);
  const [stepLoading, setStepLoading] = useState(false);
  let [loading, setLoading] = React.useState(false);
  const [completedSteps, setCompletedSteps] = useState([]);
  const [userLocation, setUserLocation] = useState({ lat: 0, lon: 0 });
  const [locationStatus, setLocationStatus] = useState('idle'); // 'idle' | 'loading' | 'success' | 'timeout'

  // Fetch user's location when reaching the address step
  useEffect(() => {
    if (step === 3 && !formData.country && locationStatus === 'idle' && userLocation.lat === 0) {
      fetchUserLocation();
    }
  }, [step]);

  const fetchUserLocation = () => {
    if (!navigator.geolocation) return;
    setLocationStatus('loading');

    // After 3 seconds, stop showing loading so user can type manually
    const uiTimeout = setTimeout(() => {
      setLocationStatus(prev => prev === 'loading' ? 'timeout' : prev);
    }, 3000);

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        setUserLocation({ lat: latitude, lon: longitude });
        try {
          const res = await fetch(`${process.env.REACT_APP_Backend_Host_Name}/api/v1/auth/geocode?lat=${latitude}&lon=${longitude}`);
          const data = await res.json();
          if (data && data.address) {
            const addr = data.address;
            const countryName = addr.country;
            const countryMatch = countries.find(c => c.name.toLowerCase() === countryName?.toLowerCase());
            const stateName = addr.state || addr.region || '';
            const cityName = addr.city || addr.town || addr.village || '';
            const road = addr.road || addr.suburb || '';
            const houseNumber = addr.house_number || '';
            const fullAddress = [houseNumber, road].filter(Boolean).join(' ');
            const zipCode = addr.postcode || '';

            // Only fill fields that are still empty (user may have typed manually)
            setFormData(prev => ({
              ...prev,
              country: prev.country || (countryMatch ? { value: countryMatch.code, label: countryMatch.name } : prev.country),
              state: prev.state || (stateName ? (countryMatch?.code === 'CA'
                ? (() => { const s = states.find(s => s.name.toLowerCase() === stateName.toLowerCase()); return s ? { name: s.name, tax: s.tax } : { name: stateName, tax: 5 }; })()
                : { name: stateName, tax: 5 }) : prev.state),
              city: prev.city || cityName,
              address: prev.address || fullAddress,
              zipCode: prev.zipCode || zipCode
            }));
            clearTimeout(uiTimeout);
            setLocationStatus('success');
          } else {
            clearTimeout(uiTimeout);
            setLocationStatus('idle');
          }
        } catch (err) {
          console.log('Reverse geocoding failed:', err);
          clearTimeout(uiTimeout);
          setLocationStatus('idle');
        }
      },
      (error) => {
        console.log('Geolocation denied or failed:', error);
        clearTimeout(uiTimeout);
        setLocationStatus('idle');
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const signupSteps = [
    { number: 1, text: 'Email' },
    { number: 2, text: 'Details' },
    { number: 3, text: 'Address' },
    { number: 4, text: 'Password' },
  ];

  const navigate = useNavigate();
  const prevStep = () => step > 1 && setStep(step - 1);


  const togglePasswordVisibility = (type) => {
    if (type === 'password') {
      setShowPassword(!showPassword);
    } else {
      setShowConfirmPassword(!showConfirmPassword);
    }
  };

  const handleInputChange = (input, value) => {
    setFormData(prevData => {
      if (input === 'country') {
        return {
          ...prevData,
          country: value,
          state: value.label === 'Canada' ? null : { name: '', tax: 5 }
        };
      }

      if (input === 'state' && formData.country?.label === 'Canada') {
        return {
          ...prevData,
          state: {
            name: value.label,
            tax: value.value
          }
        };
      }

      return { ...prevData, [input]: value };
    });

    if (input === 'password' || input === 'confirmPassword')
      setPasswordError('');
  };

  // Step 1: Validate email uniqueness before proceeding
  const handleEmailStep = async (e) => {
    e.preventDefault();
    setStepLoading(true);
    try {
      await axios.post(`${process.env.REACT_APP_Backend_Host_Name}/api/v1/auth/check-email`, { email: formData.email });
      setCompletedSteps(prev => [...new Set([...prev, 1])]);
      setStep(2);
    } catch (error) {
      showToast(error.response?.data?.error || "Something went wrong", "error");
    } finally {
      setStepLoading(false);
    }
  };

  // Step 2: Validate username and phone before proceeding
  const handleUsernameStep = async (e) => {
    e.preventDefault();

    if (!formData.fullName.trim()) {
      showToast("Please enter your full name", "error");
      return;
    }

    // Validate phone number is entered
    if (!formData.phone || formData.phone.length < 5) {
      showToast("Please enter a valid phone number", "error");
      return;
    }

    setStepLoading(true);
    try {
      await axios.post(`${process.env.REACT_APP_Backend_Host_Name}/api/v1/auth/check-username`, { username: formData.username });
      setCompletedSteps(prev => [...new Set([...prev, 2])]);
      setStep(3);
    } catch (error) {
      showToast(error.response?.data?.error || "Something went wrong", "error");
    } finally {
      setStepLoading(false);
    }
  };


  const handleSubmit = (e) => {
    e.preventDefault();

    if (formData.password !== formData.confirmPassword) {
      setPasswordError('Passwords do not match');
      return;
    }

    if (formData.password.length < 8) {
      setPasswordError('Password must be at least 8 characters long');
      return;
    }

    const hasUpperCase = /[A-Z]/.test(formData.password);
    const hasLowerCase = /[a-z]/.test(formData.password);
    const hasNumber = /[0-9]/.test(formData.password);
    const hasSpecialChar = /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(formData.password);

    if (!(hasUpperCase && hasLowerCase && hasNumber && hasSpecialChar)) {
      setPasswordError('Password must include uppercase, lowercase, number, and special character');
      return;
    }

    const currencyCode = getCurrencyForCountry(formData.country.value);
    const submissionData = {
      ...formData,
      country: {
        name: formData.country.label,
        code: formData.country.value
      },
      currency: currencyCode,
      location: userLocation.lat !== 0 ? userLocation : undefined
    };

    setLoading(true);
    axios.post(`${process.env.REACT_APP_Backend_Host_Name}/api/v1/auth/register`, submissionData).then(() => {
      showToast("An Email has been sent to you for verification... Please check your inbox!", "success")
      navigate("/signin")
    }).catch((e) => {
      console.log(e);
      showToast(e.response?.data?.error || "Something went wrong", "error")
    })
      .finally(() => {
        setLoading(false);
      })
  };


  const handleSocialLogin = (provider) => {
    window.location.href = `${process.env.REACT_APP_Backend_Host_Name}/api/v1/auth/login/${provider}`;
  };


  const renderPage = () => {
    switch (step) {

      case 1:
        return (
          <div className="signUpSection">
            <div className="leftSection">
              <div className="signUpContent">
                <div className="headerText">
                  <h2 className="welcomeTitle">Welcome</h2>
                  <p className="topText">Get started - it's free. No credit card needed.</p>
                </div>
                <button className="socialLogin googleLogin" onClick={() => handleSocialLogin('google')}>
                  <FaGoogle className="icon" />
                  Continue with Google
                </button>
                {/* <button className="socialLogin facebookLogin" onClick={() => handleSocialLogin('facebook')}>
                  <FaFacebook className="icon" />
                  Continue with Facebook
                </button> */}
                <div className="separator">Or</div>
                <form className='signUpForm' onSubmit={handleEmailStep}>
                  <label htmlFor="email" className="label">Email</label>
                  <input
                    id="email"
                    type="email"
                    placeholder="Enter email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    required
                    className='inputField'
                    disabled={stepLoading}
                  />
                  <button type="submit" className="primaryBtn" disabled={stepLoading} style={stepLoading ? { padding: "16px 0px" } : { cursor: "pointer" }}>
                    {stepLoading ? <Loader type="miniPlus" /> : "Next"}
                  </button>
                </form>
                <div className="loginOption">
                  Already have an account? <Link to="/signin">Sign In</Link>
                </div>
              </div>
            </div>
            <div className="rightSection">
              <img key={step} src={firstImage} alt="Food Dish" className="centerImage" />
            </div>
          </div>
        );

      case 2:
        return (
          <div className="signUpSection">
            <div className="rightSection">
              <img src={thirdImage} alt="Food Dish" className="centerImage" />
            </div>
            <div className="leftSection">
              <div className="signUpContent">
                <div className="headerText">
                  <h2 className="welcomeTitle">Enter Your Details</h2>
                </div>
                <form className='signUpForm' onSubmit={handleUsernameStep}>
                  <label htmlFor="fullName" className="label">Full Name</label>
                  <input
                    id="fullName"
                    type="text"
                    placeholder="Enter your full name"
                    value={formData.fullName}
                    onChange={(e) => handleInputChange('fullName', e.target.value)}
                    required
                    className='inputField'
                    disabled={stepLoading}
                  />
                  <label htmlFor="username" className="label">Username</label>
                  <input
                    id="username"
                    type="text"
                    placeholder="Enter username"
                    value={formData.username}
                    onChange={(e) => handleInputChange('username', e.target.value)}
                    required
                    className='inputField'
                    disabled={stepLoading}
                  />
                  <label htmlFor="phone" className="label">Phone Number</label>
                  <PhoneInput
                    country={'us'}
                    value={formData.phone}
                    onChange={(phone) => handleInputChange('phone', phone)}
                    inputClass="phoneInputField"
                    containerClass="phoneInputContainer"
                    buttonClass="phoneInputButton"
                    dropdownClass="phoneInputDropdown"
                    enableSearch={true}
                    searchPlaceholder="Search country..."
                    inputProps={{
                      required: true,
                      id: 'phone'
                    }}
                    disabled={stepLoading}
                  />

                  {!showSecondaryPhone ? (
                    <button
                      type="button"
                      className="addSecondaryPhoneBtn"
                      onClick={() => setShowSecondaryPhone(true)}
                    >
                      <FaPlus className="addIcon" /> Add Secondary Phone Number
                    </button>
                  ) : (
                    <>
                      <label htmlFor="secondaryPhone" className="label">Secondary Phone Number (Optional)</label>
                      <PhoneInput
                        country={'us'}
                        value={formData.secondaryPhone}
                        onChange={(phone) => handleInputChange('secondaryPhone', phone)}
                        inputClass="phoneInputField"
                        containerClass="phoneInputContainer"
                        buttonClass="phoneInputButton"
                        dropdownClass="phoneInputDropdown"
                        enableSearch={true}
                        searchPlaceholder="Search country..."
                        inputProps={{
                          id: 'secondaryPhone'
                        }}
                        disabled={stepLoading}
                      />
                    </>
                  )}

                  <label htmlFor="languages" className="label">Languages</label>
                  <MultiSelect
                    id="languages"
                    options={languages}
                    value={formData.languages?.split(', ') || []}
                    onChange={(selectedLanguages) => handleInputChange('languages', selectedLanguages.map(lang => lang.label).join(', '))}
                    placeholder="Select languages"
                    required
                  />

                  <div className="formButtonGroup">
                    <button type="button" className="secondaryBtn backBtn" onClick={prevStep} disabled={stepLoading}>
                      <FaArrowLeft /> Back
                    </button>
                    <button type="submit" className="primaryBtn nextBtn" disabled={stepLoading} style={stepLoading ? { padding: "16px 0px" } : { cursor: "pointer" }}>
                      {stepLoading ? <Loader type="miniPlus" /> : "Next"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="signUpSection">
            <div className="leftSection">
              <div className="signUpContent">
                <div className="headerText">
                  <h2 className="welcomeTitle">Enter Your Address</h2>
                  {locationStatus === 'loading' && (
                    <div className="locationIndicator loading">
                      <FaMapMarkerAlt className="pulsingIcon" /> Detecting your location...
                    </div>
                  )}
                  {locationStatus === 'success' && (
                    <div className="locationIndicator success">
                      <FaCheck style={{ color: '#22c55e' }} /> Location detected — fields auto-filled
                    </div>
                  )}
                  {locationStatus === 'timeout' && (
                    <div className="locationIndicator timeout">
                      <FaMapMarkerAlt style={{ color: '#f59e0b' }} /> Still detecting... You can fill in manually
                    </div>
                  )}
                </div>
                <form className='signUpForm' onSubmit={(e) => { e.preventDefault(); setCompletedSteps(prev => [...new Set([...prev, 3])]); setStep(4); }}>
                  <div className="formRow2Col">
                    <div className="formCol">
                      <label htmlFor="country" className="label">Country</label>
                      <Dropdown
                        id="country"
                        options={countries.map(country => ({ value: country.code, label: country.name }))}
                        value={formData.country}
                        onChange={(value) => handleInputChange('country', value)}
                        placeholder='Select Country'
                        required
                      />
                    </div>
                    <div className="formCol">
                      <label htmlFor="state" className="label">State/Province</label>
                      {formData.country.label === "Canada" ?
                        <Dropdown
                          id="state"
                          options={states.map(state => ({ value: state.tax, label: state.name }))}
                          value={formData.state ? { value: formData.state.tax, label: formData.state.name } : null}
                          onChange={(value) => handleInputChange('state', value)}
                          placeholder='Select State'
                          required
                        /> :
                        <input
                          id="state"
                          type="text"
                          placeholder="State/Province"
                          value={formData.state?.name || ''}
                          onChange={(e) => handleInputChange('state', { name: e.target.value, tax: 5 })}
                          required
                          className='inputField'
                        />}
                    </div>
                  </div>
                  <div className="formRow2Col">
                    <div className="formCol">
                      <label htmlFor="city" className="label">City</label>
                      <input
                        id="city"
                        type="text"
                        placeholder="City"
                        value={formData.city}
                        onChange={(e) => handleInputChange('city', e.target.value)}
                        required
                        className='inputField'
                      />
                    </div>
                    <div className="formCol">
                      <label htmlFor="zipCode" className="label">Zip/Postal Code</label>
                      <input
                        id="zipCode"
                        type="text"
                        placeholder="Zip/Postal Code"
                        value={formData.zipCode}
                        onChange={(e) => handleInputChange('zipCode', e.target.value)}
                        className='inputField'
                      />
                    </div>
                  </div>
                  <label htmlFor="address" className="label">Address</label>
                  <input
                    id="address"
                    type="text"
                    placeholder="Street address"
                    value={formData.address}
                    onChange={(e) => handleInputChange('address', e.target.value)}
                    required
                    className='inputField'
                  />
                  <p className="noteText">
                    Your location helps us find sellers near you
                  </p>
                  <div className="formButtonGroup">
                    <button type="button" className="secondaryBtn backBtn" onClick={prevStep}>
                      <FaArrowLeft /> Back
                    </button>
                    <button type="submit" className="primaryBtn nextBtn">Next</button>
                  </div>
                </form>
              </div>
            </div>
            <div className="rightSection" style={{ backgroundImage: `url(${firstBackground})`, backgroundColor: 'transparent' }}>
              <img key={step} src={secondImage} alt="Food Dish" className="centerImage" />
            </div>
          </div>
        );

      case 4:
        return (
          <div className="signUpSection">
            <div className="leftSection">
              <div className="signUpContent">
                <div className="headerText">
                  <h2 className="welcomeTitle">Create Password</h2>
                  <p className="topText">Set a strong password for your account</p>
                </div>
                <form className='signUpForm' onSubmit={handleSubmit}>
                  <div className='passwordFieldUpper'>
                    <label htmlFor="password" className="label">Password</label>
                    <div
                      className='hidePasswordBtn'
                      onClick={() => togglePasswordVisibility('password')}
                    >
                      {showPassword ? <FaEyeSlash /> : <FaEye />}
                    </div>
                  </div>
                  <input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="Enter password"
                    value={formData.password}
                    onChange={(e) => handleInputChange('password', e.target.value)}
                    required
                    minLength="8"
                    className='inputField'
                    disabled={loading}
                  />
                  <div className='passwordFieldUpper'>
                    <label htmlFor="confirmPassword" className="label">Confirm Password</label>
                    <div
                      className='hidePasswordBtn'
                      onClick={() => togglePasswordVisibility('confirmPassword')}
                    >
                      {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                    </div>
                  </div>
                  <input
                    id="confirmPassword"
                    type={showConfirmPassword ? "text" : "password"}
                    placeholder="Confirm password"
                    value={formData.confirmPassword}
                    onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                    required
                    minLength="8"
                    className='inputField'
                    disabled={loading}
                  />
                  {passwordError && (
                    <p className="errorText" style={{ color: 'red', marginTop: '10px' }}>
                      {passwordError}
                    </p>
                  )}
                  <div className="passwordRequirements" style={{ fontSize: '0.85em', marginTop: '10px' }}>
                    <p style={{ marginBottom: '6px', fontWeight: '500' }}>Password must:</p>
                    <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                      {[
                        { label: 'Be at least 8 characters long', met: formData.password.length >= 8 },
                        { label: 'Include an uppercase letter', met: /[A-Z]/.test(formData.password) },
                        { label: 'Include a lowercase letter', met: /[a-z]/.test(formData.password) },
                        { label: 'Include a number', met: /[0-9]/.test(formData.password) },
                        { label: 'Include a special character', met: /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>\/?]/.test(formData.password) },
                      ].map((check, index) => (
                        <li key={index} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px', color: check.met ? '#22c55e' : '#9ca3af' }}>
                          <FaCheck style={{ fontSize: '12px', flexShrink: 0 }} />
                          {check.label}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="formButtonGroup">
                    <button type="button" className="backBtn" onClick={prevStep} disabled={loading}>
                      <FaArrowLeft /> Back
                    </button>
                    <button type="submit" className="primaryBtn nextBtn" disabled={loading} style={loading ? { padding: "16px 0px" } : { cursor: "pointer" }}>
                      {loading ? <Loader type="miniPlus" /> : "Create Account"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
            <div className="rightSection" style={{ backgroundImage: `url(${secondBackground})`, backgroundColor: 'transparent' }}>
              <img src={fourthImage} alt="Food Dish" className="centerImage" />
            </div>
          </div>
        );

      default:
        return null;
    }
  };


  const handleStepNavigation = (targetStep) => {
    if (completedSteps.includes(targetStep) || targetStep === step) {
      setStep(targetStep);
    }
  };

  return (
    <div className="signUpContainer">
      <SEO title="Sign up" description="Create a Chocowi account." noIndex noFollow />
      <Link to="/" className="chocowiLogoDiv">
        <img src={Logo} alt="Chocowi Logo" className="chocowiLogo" />
      </Link>
      {renderPage()}
    </div>
  );
};

export default SignUpForm;